// src/features/dashboard/hooks/Employee/useActivityTimeline.ts

import { useEffect, useState } from "react";

import { getActivityTimeline } from "../../services/Employee/activityTimeline.service";
import { useDashboardDate } from "../../context/DashboardDateContext";
import { formatApiDate } from "../../utils/date.utils";
import type { ActivityTimelineResponse } from "../../types/Employee/activityTimeline.types";
import { AuthSession } from "../../../../utils/authSession";

export const useActivityTimeline = () => {
  const employeeCode = AuthSession.currentEmployee();
  const { fromDate, toDate } = useDashboardDate();

  const [data, setData] = useState<ActivityTimelineResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!employeeCode) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);

        const response = await getActivityTimeline({
          employeeCode,
          fromDate: formatApiDate(fromDate),
          toDate: formatApiDate(toDate),
        });

        const activityColors: Record<string, string> = {
          "Active Hours": "#549A4C",
          "Meeting Hours": "#2F4A9E",
          "Idle Hours": "#D8B06B",
        };

        const mergedActivities = response.activities.reduce(
          (acc: Array<(typeof response.activities)[number]>, current) => {
            const last = acc[acc.length - 1];

            if (last && last.activityType === current.activityType) {
              last.durationHours += current.durationHours;
              last.endTime = current.endTime;
              return acc;
            }

            acc.push({ ...current });
            return acc;
          },
          [],
        );

        const totalDuration = mergedActivities.reduce(
          (sum, item) => sum + item.durationHours,
          0,
        );

        const timeline = mergedActivities.map((item) => ({
          width:
            totalDuration > 0
              ? `${((item.durationHours / totalDuration) * 100).toFixed(2)}%`
              : "0%",
          color: activityColors[item.activityType] || "#CBD5E1",
        }));

        const timelineLabels = [
          response.loginTime,
          ...mergedActivities
            .filter((_, index) => index % 2 === 1)
            .map((item) => item.startTime),
          response.currentOrLogoutTime,
        ];

        setData({
          loginInfo: `Login ${response.loginTime} `,
          timeline,
          timelineLabels,
          legends: [
            {
              label: "Active",
              color: "#4F944A",
              value: response.activeHours,
            },
            {
              label: "Meetings",
              color: "#243E90",
              value: response.meetingHours,
            },
            {
              label: "Idle",
              color: "#D8B06B",
              value: response.idleHours,
            },
          ],
          stats: [
            {
              label: "LOGIN",
              value: response.loginTime,
            },
          ],
        } as ActivityTimelineResponse);
      } catch (error) {
        console.error("Activity Timeline Error:", error);
      } finally {
        setLoading(false);
      }
    };

    void fetchData();
  }, [employeeCode, fromDate, toDate]);

  return {
    data,
    loading,
  };
};
